
import { IAssetProvider, IAsset, IAssetMigrator, AssetMigrator, ISceneInfo, Configuration } from "@viscircle-org/threngine";
import Axios, { AxiosRequestConfig } from "axios";


const CUSTOM_ASSET_SERVER_URI = 'https://dz0y2jf1sp6fc.cloudfront.net/assets';

//FIXME: DEBUG STUFF, REMOVE!
// const CUSTOM_ASSET_SERVER_URI = 'http://localhost:8080/assets';
//--

interface Project {
  id:string,
  name:string,
  config:Configuration
}

interface LocalStorageEntry {
  id:string,
  responseType:string,
  data:ArrayBuffer
}

export class IFRAssetProvider implements IAssetProvider {
  private _project:Project;
  private _files:{id:string, fileName:string, data:Promise<any>}[] = [];
  private _assets:IAsset[];
  private _migrator:IAssetMigrator;
  private _cdnUrls:string[]

  constructor() {
    this._migrator = new AssetMigrator();
  }

  get project():Project {return this._project}

  initialize = async () => {
    return this._initializeCustomAssetServer();
  }

  private _initializeCustomAssetServer = async ():Promise<any> => {
    const res = await Axios({
      method: 'get',
      url: `${CUSTOM_ASSET_SERVER_URI}/assets.json`
    });

    const assets:IAsset[] = await this._migrator.migrate(res.data, this.getFile);

    this._cdnUrls = [];
    for (let asset of assets) {
      for (let fileName of asset.files) {
        this._cdnUrls.push(encodeURI(`${CUSTOM_ASSET_SERVER_URI}/${asset.id}/${fileName}`))
      }
    };
    
    this._assets = assets.map((a) => a);
    const rootAsset = this._assets.find((el) => el.infoType === 'SCENE');

    if (rootAsset) {
      const info = rootAsset.info as ISceneInfo;            
      this._project = {
        id: rootAsset.id,
        name: info.name,
        config: info.data as Configuration
      }
    };

    // console.log("rootAsset", rootAsset);

    return Promise.resolve();
  }


  getAsset = (id:string) => {
    const asset = this._assets.find(a => a.id===id);
    if (!asset) throw new Error("Unknown asset requested: " + id);
    return asset;
  }

  getAssets = () => this._assets;

  getFile = async (id:string, fileName:string, responseType?:string):Promise<any> => {
    const cached = this._files.find(f => f.id===id && f.fileName===fileName);
    if (cached) return cached.data;

    const url = this._cdnUrls.find((url) => url.includes(encodeURI(`${id}/${fileName}`)));

    const config:AxiosRequestConfig = {method: 'get', url};
    if (responseType) config.responseType = responseType as any;

    const response = await Axios(config);
    
    this._files.push({id, fileName, data: response.data});
    return response.data;
  }
}

class LocalFileStorage {
  database:IDBDatabase;
  identifier:string;
  debug:boolean = false;

  constructor (identifier:string) {
      this.identifier = identifier;
  }

  initialize = () => {
    const w = (window as any);
    const idb:IDBFactory = w.indexedDB || w.mozIndexedDB || w.webkitIndexedDB || w.msIndexedDB || w.shimIndexedDB;
    const request = idb.open(this.identifier);
    
    return new Promise(res => {
      request.onupgradeneeded = () => {
        const db = request.result;
        if (!db.objectStoreNames.contains("files")) {
            db.createObjectStore("files", {keyPath: 'id'});
        }
      }
      
      request.onsuccess = () => {
        this.database = request.result;
        res(null);
      }
    })
  }

  
  add = (f:LocalStorageEntry) => {
    this._log("ADDING "+f.id+" TO "+this.identifier+" STORE")
    const store = this._getStore(true);
    if (store) {
      store.add(f);
    }
  }

  get = async (id:string):Promise<LocalStorageEntry> => {
    return new Promise<any|undefined>(res => {

      const store = this._getStore();
      
      if (!store) {
        this._log("NO STORE FOUND FOR "+this.identifier)

        res(undefined);
        return;
      }

      const req = store.get(id);

      req.onsuccess = () => { 
        this._log("GETTING "+id+" FROM "+this.identifier+" STORE");
        
        if (req.result && req.result.data) {
          this._log("...found");
          res(req.result);
        } else {
          this._log("...not found. Getting from remote and adding to store");
          res(undefined);
        }
      };

      req.onerror = e => {
        console.error(e);
        res(undefined);
      }
    })
  }

  private _getStore = (write?:boolean) => {
    if (!this.database) return;
    const transaction = write
      ? this.database.transaction("files", "readwrite")
      : this.database.transaction("files");
    const store = transaction.objectStore("files");
    return store;
  }

  private _log = (msg:string) => {
    if (this.debug) {
      console.log(msg);
    }
  }
}
